import { useState, useRef, useEffect } from 'react'
import { Lock, AlertCircle } from 'lucide-react'

const CORRECT_PIN = '5121'

export default function PinLock({ onUnlock }) {
  const [pin, setPin] = useState(['', '', '', ''])
  const [error, setError] = useState(false)
  const [shake, setShake] = useState(false)
  const inputRefs = [useRef(), useRef(), useRef(), useRef()]

  useEffect(() => {
    // Focus first input on mount
    inputRefs[0].current?.focus()
  }, [])

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    const newPin = [...pin]
    newPin[index] = value
    setPin(newPin)
    setError(false)

    // Move to next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus()
    }

    // Check PIN when all digits entered
    if (value && index === 3) {
      const enteredPin = newPin.join('')
      if (enteredPin === CORRECT_PIN) {
        sessionStorage.setItem('app_unlocked', 'true')
        onUnlock()
      } else {
        setError(true)
        setShake(true)
        setTimeout(() => {
          setShake(false)
          setPin(['', '', '', ''])
          inputRefs[0].current?.focus()
        }, 500)
      }
    }
  }

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').slice(0, 4)
    if (/^\d{1,4}$/.test(pasted)) {
      const digits = pasted.split('')
      const newPin = [...pin]
      digits.forEach((d, i) => {
        if (i < 4) newPin[i] = d
      })
      setPin(newPin)

      if (digits.length === 4) {
        if (pasted === CORRECT_PIN) {
          sessionStorage.setItem('app_unlocked', 'true')
          onUnlock()
        } else {
          setError(true)
          setShake(true)
          setTimeout(() => {
            setShake(false)
            setPin(['', '', '', ''])
            inputRefs[0].current?.focus()
          }, 500)
        }
      } else {
        inputRefs[digits.length]?.current?.focus()
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Clystcore Surveyors</h1>
          <p className="text-slate-500 mt-2">Enter PIN to continue</p>
        </div>

        <div
          className={`flex justify-center gap-3 mb-6 ${shake ? 'animate-shake' : ''}`}
          onPaste={handlePaste}
        >
          {pin.map((digit, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={`
                w-14 h-16 text-center text-2xl font-bold rounded-lg border-2
                focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all
                ${error
                  ? 'border-red-500 bg-red-50 text-red-600'
                  : 'border-slate-300 bg-slate-50 text-slate-900'
                }
              `}
            />
          ))}
        </div>

        {error && (
          <div className="flex items-center justify-center gap-2 text-red-600 text-sm mb-4">
            <AlertCircle className="w-4 h-4" />
            <span>Incorrect PIN</span>
          </div>
        )}

        <p className="text-center text-xs text-slate-400">
          Contact administrator if you've forgotten the PIN
        </p>
      </div>
    </div>
  )
}
